import type { GameObj, KEventController } from 'kaplay'
import type { KAPLAYCtx } from '../types.ts'
import { getTiledSize } from '../utils.ts'
import { COLOR_HEX_PURPLE } from '../consts.ts'

export function useElevatorPopup(k: KAPLAYCtx) {
  let popupNavButtonClickEvent: KEventController
  let objPopup: GameObj
  let toTime = 2500
  const availableFloors = ['08', '09', '10', '11', '12', '13']

  k.loadSprite('elevatorNav', 'sprites/elevator-nav.png', {
    sliceX: 3,
    sliceY: 4,
    anims: {
      btn08: { frames: [0] },
      btn09: { frames: [1] },
      btn10: { frames: [2] },
      btn11: { frames: [3] },
      btn12: { frames: [4] },
      btn13: { frames: [5] },
      btn08active: { frames: [6] },
      btn09active: { frames: [7] },
      btn10active: { frames: [8] },
      btn11active: { frames: [9] },
      btn12active: { frames: [10] },
      btn13active: { frames: [11] },
    },
  })

  k.loadSprite('uiFrame', 'sprites/ui-frame.png', {
    slice9: {
      top: getTiledSize(),
      bottom: getTiledSize(),
      left: getTiledSize(),
      right: getTiledSize(),
    },
  })

  const popupSize = {
    w: getTiledSize(6),
    h: getTiledSize(4),
  }

  function getButtonObj() {
    return [
      k.area(),
      k.anchor('center'),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_elevator_nav_btn',
      'ui_popup',
      'ui',
    ]
  }

  function showElevatorPopup(opts?: { origin?: 'stairs' | 'elevator' }) {
    opts = opts || {}
    opts.origin = opts.origin || 'elevator'

    objPopup = k.add([])

    const middle = {
      x: k.width() / 2,
      y: k.height() / 2,
    }

    objPopup.add([
      k.rect(k.width(), k.height()),
      k.pos(0, 0),
      k.color(k.Color.fromHex(COLOR_HEX_PURPLE)),
      k.opacity(0.8),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_backdrop',
      'ui_popup',
      'ui',
    ])

    objPopup.add([
      k.sprite('uiFrame', {
        width: popupSize.w + getTiledSize(2),
        height: popupSize.h + getTiledSize(2),
      }),
      k.pos(middle.x, middle.y),
      k.anchor('center'),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_background',
      'ui_popup',
      'ui',
    ])

    objPopup.add([
      k.sprite('elevatorNav', {
        width: popupSize.w,
        height: popupSize.h,
      }),
      k.pos(middle.x, middle.y),
      k.anchor('center'),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_elevator_nav',
      'ui_popup',
      'ui',
    ])

    // BUTTONS
    objPopup.add([
      ...getButtonObj(),
      k.sprite('elevatorNav', { width: getTiledSize(2), height: getTiledSize(2), anim: 'btn08' }),
      k.pos(middle.x - getTiledSize(2), middle.y - getTiledSize(1)),
      'ui_popup_elevator_nav_btn_08',
    ])

    objPopup.add([
      ...getButtonObj(),
      k.sprite('elevatorNav', { width: getTiledSize(2), height: getTiledSize(2), anim: 'btn09' }),
      k.pos(middle.x, middle.y - getTiledSize(1)),
      'ui_popup_elevator_nav_btn_09',
    ])

    objPopup.add([
      ...getButtonObj(),
      k.sprite('elevatorNav', { width: getTiledSize(2), height: getTiledSize(2), anim: 'btn10' }),
      k.pos(middle.x + getTiledSize(2), middle.y - getTiledSize(1)),
      'ui_popup_elevator_nav_btn_10',
    ])

    objPopup.add([
      ...getButtonObj(),
      k.sprite('elevatorNav', { width: getTiledSize(2), height: getTiledSize(2), anim: 'btn11' }),
      k.pos(middle.x - getTiledSize(2), middle.y + getTiledSize(1)),
      'ui_popup_elevator_nav_btn_11',
    ])

    objPopup.add([
      ...getButtonObj(),
      k.sprite('elevatorNav', { width: getTiledSize(2), height: getTiledSize(2), anim: 'btn12' }),
      k.pos(middle.x, middle.y + getTiledSize(1)),
      'ui_popup_elevator_nav_btn_12',
    ])

    objPopup.add([
      ...getButtonObj(),
      k.sprite('elevatorNav', { width: getTiledSize(2), height: getTiledSize(2), anim: 'btn13' }),
      k.pos(middle.x + getTiledSize(2), middle.y + getTiledSize(1)),
      'ui_popup_elevator_nav_btn_13',
    ])

    popupNavButtonClickEvent = k.onClick('ui_popup_elevator_nav_btn', (a: GameObj) => {
      if (opts.origin === 'elevator') {
        k.play('sfx_elevator', { volume: 0.3 })
        toTime = 2500
      } else if (opts.origin === 'stairs') {
        k.play('sfx_stairs', { volume: 0.5 })
        toTime = 1500
      }
      if (a.tags.includes('ui_popup_elevator_nav_btn_08')) btnNavClickHandler('08')
      else if (a.tags.includes('ui_popup_elevator_nav_btn_09')) btnNavClickHandler('09')
      else if (a.tags.includes('ui_popup_elevator_nav_btn_10')) btnNavClickHandler('10')
      else if (a.tags.includes('ui_popup_elevator_nav_btn_11')) btnNavClickHandler('11')
      else if (a.tags.includes('ui_popup_elevator_nav_btn_12')) btnNavClickHandler('12')
      else if (a.tags.includes('ui_popup_elevator_nav_btn_13')) btnNavClickHandler('13')
    })

  }

  function hideElevatorPopup() {
    popupNavButtonClickEvent.cancel()
    objPopup.destroy()
  }

  function btnNavClickHandler(btn: string) {
    objPopup.get(`ui_popup_elevator_nav_btn_${btn}`, { recursive: true })[0]?.play(`btn${btn}active`)
    const playerPos = k.get('player')[0]?.pos

    setTimeout(() => {
      if (availableFloors.includes(btn)) {
        k.store.currentScene = `floor${btn}`
        k.go(`floor${btn}`, { playerPos })
      }
      hideElevatorPopup()
    }, toTime)
  }

  return {
    showElevatorPopup,
    hideElevatorPopup,
  }
}
